import { useEffect, useState } from 'react';
import { Button, Modal, Radio, Select, Space, Typography, message } from 'antd';
import { space } from '@viq-admin/ui';
import { ColumnPicker } from './ColumnPicker';
import type {
  BatchExportApi,
  ExportColumnDescriptor,
  ExportFormat,
  ExportMode,
} from './types';

interface Props {
  open:           boolean;
  conferenceId:   string;
  conferenceName: string;
  exportApi:      BatchExportApi;
  onClose:        () => void;
  /** False for a stakeholder, a conference with no discovery questions, or an unscoped console export (no company picked). */
  showDiscovery?: boolean;
  /** One-line hint above the footer, e.g. why discovery columns are absent. */
  note?: string;
}

const MODE_OPTIONS: Array<{ value: ExportMode; label: string; description: string }> = [
  { value: 'all',           label: 'All records',           description: 'Every lead in this event' },
  { value: 'net_new',       label: 'Net new since last export', description: 'Only leads created after your last export' },
  { value: 'updated_since', label: 'Updated since last export', description: 'Leads created or modified since your last export' },
];

const FORMAT_OPTIONS: Array<{ value: ExportFormat; label: string }> = [
  { value: 'csv',  label: 'CSV' },
  { value: 'xlsx', label: 'Excel (xlsx), not available yet' },
];

/**
 * Shared Batch export dialog — ported from the portal's
 * `src/views/BatchScreen/ExportDialog.tsx`. Modes drive the server-side
 * watermark; xlsx is deferred server-side (422 in v1), so it's rendered but
 * disabled. The download + mark calls are shell-provided via `exportApi`.
 */
export function ExportDialog({
  open,
  conferenceId,
  conferenceName,
  exportApi,
  onClose,
  showDiscovery,
  note,
}: Props) {
  const [mode, setMode]     = useState<ExportMode>('all');
  const [format, setFormat] = useState<ExportFormat>('csv');
  const [busy, setBusy]     = useState(false);

  const [columns, setColumns] = useState<ExportColumnDescriptor[]>([]);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const [includeDiscovery, setIncludeDiscovery] = useState(true);
  const [includeDiscoverySummary, setIncludeDiscoverySummary] = useState(true);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    void exportApi
      .getColumnPrefs()
      .then((p) => {
        if (cancelled) return;
        setColumns(p.availableColumns);
        setHiddenColumns(p.hiddenColumns);
        setIncludeDiscovery(p.includeDiscovery);
        setIncludeDiscoverySummary(p.includeDiscoverySummary);
      })
      // A dead preferences call must never block an export at a booth.
      .catch(() => undefined);
    return () => { cancelled = true; };
  }, [open, exportApi]);

  /** Nothing ticked — the server would 422, so stop it at the button. */
  const noColumnsSelected = columns.length > 0 && hiddenColumns.length === columns.length;

  async function onExport() {
    setBusy(true);
    try {
      // xlsx is disabled in the Select (server-side 422 in v1), so csv is the
      // only reachable format — matches the exportApi's `format: 'csv'` contract.
      await exportApi.downloadExport({
        conferenceId,
        mode,
        format: 'csv',
        hideColumns: hiddenColumns,
        includeDiscovery,
        includeDiscoverySummary,
      });
      // Saved only on a successful export — "what I last exported with".
      await exportApi
        .saveColumnPrefs({ hiddenColumns, includeDiscovery, includeDiscoverySummary })
        .catch(() => undefined);
      void message.success('Export started');
      onClose();
    } catch (err) {
      void message.error(
        (err as { message?: string }).message ?? 'Couldn’t export. Try again.',
      );
    } finally {
      setBusy(false);
    }
  }

  async function onMark() {
    setBusy(true);
    try {
      await exportApi.markExported(conferenceId);
      void message.success('Marked all records as exported');
      onClose();
    } catch (err) {
      void message.error(
        (err as { message?: string }).message ?? 'Couldn’t mark the records as exported. Try again.',
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open={open}
      title={`Export: ${conferenceName}`}
      onCancel={onClose}
      width={520}
      footer={null}
      destroyOnHidden
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <div>
          <Typography.Text strong>Records</Typography.Text>
          <Radio.Group
            value={mode}
            onChange={(e) => setMode(e.target.value as ExportMode)}
            style={{ display: 'flex', flexDirection: 'column', gap: space.sm, marginTop: space.sm }}
          >
            {MODE_OPTIONS.map((opt) => (
              <Radio key={opt.value} value={opt.value}>
                <span>
                  <strong>{opt.label}</strong>
                  <br />
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {opt.description}
                  </Typography.Text>
                </span>
              </Radio>
            ))}
          </Radio.Group>
        </div>

        <div>
          <Typography.Text strong>Format</Typography.Text>
          <Select
            value={format}
            onChange={setFormat}
            style={{ width: '100%', marginTop: space.sm }}
            options={FORMAT_OPTIONS.map((opt) => ({
              value: opt.value,
              label: opt.label,
              // xlsx is server-side 422 in v1 — render but warn the user.
              disabled: opt.value === 'xlsx',
            }))}
          />
        </div>

        {columns.length > 0 && (
          <ColumnPicker
            availableColumns={columns}
            hiddenColumns={hiddenColumns}
            onHiddenColumnsChange={setHiddenColumns}
            showDiscovery={showDiscovery ?? true}
            includeDiscovery={includeDiscovery}
            onIncludeDiscoveryChange={setIncludeDiscovery}
            includeDiscoverySummary={includeDiscoverySummary}
            onIncludeDiscoverySummaryChange={setIncludeDiscoverySummary}
          />
        )}

        {note && (
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {note}
          </Typography.Text>
        )}

        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: space.sm }}>
          <Button onClick={onMark} disabled={busy}>
            Mark as exported
          </Button>
          <Space>
            <Button onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            <Button
              type="primary"
              onClick={onExport}
              loading={busy}
              disabled={noColumnsSelected}
              title={noColumnsSelected ? 'Choose at least one column' : undefined}
            >
              Export
            </Button>
          </Space>
        </div>
      </Space>
    </Modal>
  );
}
